import { useEffect, useState } from 'react';
import { PhotoTagEntityType, type CreatePhotoTagInput } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';

export interface PreUploadPublicFlags {
  isPublic: boolean;
  showOnLanding: boolean;
}

interface PreUploadTagModalProps {
  open: boolean;
  onCancel: () => void;
  onSkip: () => void;
  onApply: (tags: CreatePhotoTagInput[], publicFlags: PreUploadPublicFlags) => void;
  eventTypes: { id: string; name: string }[];
  events: { id: string; name: string }[];
  venues: { id: string; name: string }[];
  vendors: { id: string; businessName: string }[];
  users: { id: string; name: string }[];
}

/**
 * Tags the whole batch before a single file uploads — so a set dropped or
 * imported from Google Photos lands already tagged (entity tags, and the
 * public-site "publish"/"landing" flags) instead of needing every photo
 * opened afterward to tag it by hand.
 */
export function PreUploadTagModal({
  open,
  onCancel,
  onSkip,
  onApply,
  eventTypes,
  events,
  venues,
  vendors,
  users,
}: PreUploadTagModalProps) {
  const [eventTypeIds, setEventTypeIds] = useState<string[]>([]);
  const [eventIds, setEventIds] = useState<string[]>([]);
  const [venueIds, setVenueIds] = useState<string[]>([]);
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [showOnLanding, setShowOnLanding] = useState(false);

  // Reset the draft each time the modal opens, so tags chosen for a previous
  // batch never silently carry over onto the next one.
  useEffect(() => {
    if (open) {
      setEventTypeIds([]);
      setEventIds([]);
      setVenueIds([]);
      setVendorIds([]);
      setUserIds([]);
      setIsPublic(false);
      setShowOnLanding(false);
    }
  }, [open]);

  const handleApply = () => {
    const tags: CreatePhotoTagInput[] = [
      ...eventTypeIds.map((id) => ({ entityType: PhotoTagEntityType.EVENT_TYPE, entityId: id })),
      ...eventIds.map((id) => ({ entityType: PhotoTagEntityType.EVENT, entityId: id })),
      ...venueIds.map((id) => ({ entityType: PhotoTagEntityType.VENUE, entityId: id })),
      ...vendorIds.map((id) => ({ entityType: PhotoTagEntityType.VENDOR, entityId: id })),
      ...userIds.map((id) => ({ entityType: PhotoTagEntityType.USER, entityId: id })),
    ];
    onApply(tags, { isPublic, showOnLanding });
  };

  return (
    <EntityModal
      open={open}
      onClose={onCancel}
      title="Tag before uploading"
      size="sm"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSkip}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            data-testid="pre-upload-skip"
          >
            Skip tagging
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            data-testid="pre-upload-apply"
          >
            Apply &amp; continue
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Tag every photo in this upload up front — you won&apos;t need to tag them one at a time
          afterward.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category (website section)
          </label>
          <SearchableListSelect
            items={eventTypes}
            value={eventTypeIds}
            onChange={(v) => setEventTypeIds(v as string[])}
            getValue={(t) => t.id}
            getLabel={(t) => t.name}
            multiple
            placeholder="Select categories..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Events</label>
          <SearchableListSelect
            items={events}
            value={eventIds}
            onChange={(v) => setEventIds(v as string[])}
            getValue={(e) => e.id}
            getLabel={(e) => e.name}
            multiple
            placeholder="Select events..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Venues</label>
          <SearchableListSelect
            items={venues}
            value={venueIds}
            onChange={(v) => setVenueIds(v as string[])}
            getValue={(v) => v.id}
            getLabel={(v) => v.name}
            multiple
            placeholder="Select venues..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendors</label>
          <SearchableListSelect
            items={vendors}
            value={vendorIds}
            onChange={(v) => setVendorIds(v as string[])}
            getValue={(v) => v.id}
            getLabel={(v) => v.businessName}
            multiple
            placeholder="Select vendors..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Users</label>
          <SearchableListSelect
            items={users}
            value={userIds}
            onChange={(v) => setUserIds(v as string[])}
            getValue={(u) => u.id}
            getLabel={(u) => u.name}
            multiple
            placeholder="Select users..."
          />
        </div>

        <div className="border-t border-gray-200 pt-3 space-y-2">
          <p className="text-sm font-medium text-gray-700">Public site</p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsPublic(checked);
                if (!checked) setShowOnLanding(false);
              }}
            />
            Publish to the public gallery
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showOnLanding}
              disabled={!isPublic}
              onChange={(e) => setShowOnLanding(e.target.checked)}
            />
            Feature on the landing page
          </label>
        </div>
      </div>
    </EntityModal>
  );
}
